import React from 'react';
import CustomMultiSelect from 'src/components/MessengerSettings/CustomMultiSelect';
import { chatgenieAPI } from 'src/config/api';
import SimpleCheckbox from 'src/components/SimpleCheckbox';
import PropTypes from 'prop-types';
import { Label } from 'src/pages/DeploymentCenter/components/RadioGroup/RadioGroup.style';
import { isConnectWise, isAutotask, isHalo } from 'src/utils/psa';
import { CustomSelectOption } from './style';
import {
  SCOPE_ALL_CLIENTS,
  SCOPE_COMPANY_TYPES,
  SCOPE_CONTACT_TYPES,
  SCOPE_PARENT_COMPANY,
  SCOPE_SPECIFIC_CLIENTS,
  SCOPE_TOP_LEVEL
} from '../constants';
import { IntentField } from '../style';

const CustomOption = props => {
  const { data, isSelected, label } = props;
  const id = `select-option-checkbox-${data.id}`;

  return (
    <CustomSelectOption {...props}>
      <div>
        <SimpleCheckbox htmlFor={id} id={id} checked={isSelected} readOnly />
        {label}
      </div>
      <span>{!data.is_magic_agent_enabled && 'Not enabled'}</span>
    </CustomSelectOption>
  );
};
CustomOption.propTypes = {
  data: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired
};

export default function VisibilityScopeSettings({
  clients,
  companyTypes,
  contactTypes,
  parentCompanies,
  topLevels,
  visibilityScope,
  handleVisibilityScopeChange,
  handleClientsChange,
  handleCompanyTypesChange,
  handleContactTypesChange,
  handleParentCompaniesChange,
  handleTopLevelsChange,
  showAllClientsOption,
  selectProps
}) {
  const handleChange = e => {
    handleVisibilityScopeChange(e.target.value);
  };

  const isCompanyTypesVisible = isConnectWise();
  const isContactTypesVisible = isConnectWise();
  const isParentCompanyVisible = isAutotask();
  const isTopLevelVisible = isHalo();

  return (
    <IntentField alignItems="flex-start">
      <p>Visibility filters</p>
      <div>
        {showAllClientsOption && (
          <Label>
            <input
              type="radio"
              value={SCOPE_ALL_CLIENTS}
              checked={SCOPE_ALL_CLIENTS === visibilityScope}
              onChange={handleChange}
            />
            <span className="dot" />
            All clients
          </Label>
        )}
        {isCompanyTypesVisible && (
          <>
            <Label>
              <input
                type="radio"
                value={SCOPE_COMPANY_TYPES}
                checked={SCOPE_COMPANY_TYPES === visibilityScope}
                onChange={handleChange}
              />
              <span className="dot" />
              Company types
            </Label>
            {visibilityScope === SCOPE_COMPANY_TYPES && (
              <>
                <CustomMultiSelect
                  name="variation-company-types"
                  value={companyTypes}
                  options={(query, page) =>
                    chatgenieAPI.getCompanyTypes({ query, page, normalizer: res => res.data })
                  }
                  onChange={handleCompanyTypesChange}
                  componentType="paginate"
                  {...selectProps}
                />
                <p />
              </>
            )}
          </>
        )}
        {isParentCompanyVisible && (
          <>
            <Label>
              <input
                type="radio"
                value={SCOPE_PARENT_COMPANY}
                checked={SCOPE_PARENT_COMPANY === visibilityScope}
                onChange={handleChange}
              />
              <span className="dot" />
              Parent companies
            </Label>
            {visibilityScope === SCOPE_PARENT_COMPANY && (
              <>
                <CustomMultiSelect
                  name="variation-parent-companies"
                  value={parentCompanies}
                  options={(query, page) =>
                    chatgenieAPI.getAgentCompanies({ query, page, normalizer: res => res.data })
                  }
                  onChange={handleParentCompaniesChange}
                  componentType="paginate"
                  {...selectProps}
                />
                <p />
              </>
            )}
          </>
        )}
        {isTopLevelVisible && (
          <>
            <Label>
              <input
                type="radio"
                value={SCOPE_TOP_LEVEL}
                checked={SCOPE_TOP_LEVEL === visibilityScope}
                onChange={handleChange}
              />
              <span className="dot" />
              Top levels
            </Label>
            {visibilityScope === SCOPE_TOP_LEVEL && (
              <>
                <CustomMultiSelect
                  name="variation-top-levels"
                  value={topLevels}
                  options={(query, page) =>
                    chatgenieAPI.getTopLevels({ query, page, normalizer: res => res.data })
                  }
                  onChange={handleTopLevelsChange}
                  componentType="paginate"
                  {...selectProps}
                />
                <p />
              </>
            )}
          </>
        )}
        {isContactTypesVisible && (
          <>
            <Label>
              <input
                type="radio"
                value={SCOPE_CONTACT_TYPES}
                checked={SCOPE_CONTACT_TYPES === visibilityScope}
                onChange={handleChange}
              />
              <span className="dot" />
              Contact types
            </Label>
            {visibilityScope === SCOPE_CONTACT_TYPES && (
              <>
                <CustomMultiSelect
                  name="variation-contact-types"
                  value={contactTypes}
                  options={(query, page) =>
                    chatgenieAPI.getContactTypes({ query, page, normalizer: res => res.data })
                  }
                  onChange={handleContactTypesChange}
                  componentType="paginate"
                  {...selectProps}
                />
                <p />
              </>
            )}
          </>
        )}
        <Label>
          <input
            type="radio"
            value={SCOPE_SPECIFIC_CLIENTS}
            checked={SCOPE_SPECIFIC_CLIENTS === visibilityScope}
            onChange={handleChange}
          />
          <span className="dot" />
          Specific clients
        </Label>
        {visibilityScope === SCOPE_SPECIFIC_CLIENTS && (
          <>
            <CustomMultiSelect
              name="variation-clients"
              value={clients}
              options={(query, page) => chatgenieAPI.getAgentCompanies({ query, page })}
              onChange={handleClientsChange}
              componentType="paginate"
              components={{
                Option: CustomOption
              }}
              {...selectProps}
            />
            <p />
          </>
        )}
      </div>
    </IntentField>
  );
}

VisibilityScopeSettings.propTypes = {
  handleClientsChange: PropTypes.func.isRequired,
  handleVisibilityScopeChange: PropTypes.func.isRequired,
  handleCompanyTypesChange: PropTypes.func.isRequired,
  handleContactTypesChange: PropTypes.func.isRequired,
  handleParentCompaniesChange: PropTypes.func.isRequired,
  handleTopLevelsChange: PropTypes.func.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object),
  companyTypes: PropTypes.arrayOf(PropTypes.object),
  contactTypes: PropTypes.arrayOf(PropTypes.object),
  parentCompanies: PropTypes.arrayOf(PropTypes.object),
  topLevels: PropTypes.arrayOf(PropTypes.object),
  visibilityScope: PropTypes.string,
  showAllClientsOption: PropTypes.bool,
  selectProps: PropTypes.object
};

VisibilityScopeSettings.defaultProps = {
  showAllClientsOption: true,
  clients: [],
  companyTypes: [],
  contactTypes: [],
  parentCompanies: [],
  topLevels: [],
  visibilityScope: SCOPE_ALL_CLIENTS,
  selectProps: {}
};
